import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import "../framework.css";
import "./dashboard.scss";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const StatCard = ({ label, value, color, sub }) => (
  <div style={{background:"#fff",borderRadius:12,padding:"20px 24px",boxShadow:"0 2px 8px rgba(0,0,0,0.07)",minWidth:160,flex:1}}>
    <div style={{fontSize:12,color:"#888",marginBottom:6}}>{label}</div>
    <div style={{fontSize:26,fontWeight:700,color:color||"#1e293b"}}>{value}</div>
    {sub && <div style={{fontSize:11,color:"#aaa",marginTop:4}}>{sub}</div>}
  </div>
);

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState("");
  const [currency, setCurrency] = useState("");

  const fetchData = (y, m, c) => {
    setLoading(true);
    const token = localStorage.getItem("token");
    let url = `${process.env.REACT_APP_BASE_URL}admin/dashboard?year=${y}`;
    if (m) url += `&month=${m}`;
    if (c) url += `&currency=${c}`;
    fetch(url, { headers: { authorization: token } })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchData(year, month, currency); }, [year, month, currency]);

  if (loading) return <div style={{padding:40,fontSize:18}}>Loading dashboard...</div>;
  if (!data || data.message) return <div style={{padding:40,color:"red"}}>Error loading dashboard</div>;

  const { summary, usersByContinent, usersByMonth, paymentsByMonth, paymentsByCurrency, paymentsByMethod, allowedYears, currencies } = data;

  const monthLabels = MONTHS;
  const userMonthData = monthLabels.map(m => usersByMonth?.[m] || 0);
  const paymentMonthData = monthLabels.map(m => Math.round(paymentsByMonth?.[m] || 0));

  return (
    <div style={{padding:24,fontFamily:"sans-serif",background:"#f8fafc",minHeight:"100vh"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:24,flexWrap:"wrap",gap:12}}>
        <h2 style={{margin:0,fontSize:22,fontWeight:700}}>Dashboard</h2>
        <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"center"}}>
          <select value={year} onChange={e=>{setYear(e.target.value);}} style={{padding:"6px 12px",borderRadius:6,border:"1px solid #ddd",fontSize:13}}>
            {(allowedYears||[]).map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          <select value={month} onChange={e=>setMonth(e.target.value)} style={{padding:"6px 12px",borderRadius:6,border:"1px solid #ddd",fontSize:13}}>
            <option value="">All Months</option>
            {MONTHS.map((m,i)=><option key={m} value={i+1}>{m}</option>)}
          </select>
          <select value={currency} onChange={e=>setCurrency(e.target.value)} style={{padding:"6px 12px",borderRadius:6,border:"1px solid #ddd",fontSize:13}}>
            <option value="">All Currencies</option>
            {(currencies||[]).map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={()=>{setYear(new Date().getFullYear());setMonth("");setCurrency("");}} style={{padding:"6px 14px",borderRadius:6,border:"1px solid #ddd",background:"#fff",cursor:"pointer",fontSize:13}}>Reset</button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{display:"flex",gap:12,flexWrap:"wrap",marginBottom:24}}>
        <StatCard label="Total Users" value={summary.totalUsers?.toLocaleString()} color="#007bff" sub={`+${summary.newUsers} this period`} />
        <StatCard label="Paid Donations" value={summary.totalPaidCount?.toLocaleString()} color="#28a745" sub={`${summary.totalPaidAmount?.toLocaleString()} (amount)`} />
        <StatCard label="Overdue" value={summary.overdueCount?.toLocaleString()} color="#dc3545" sub={`${summary.totalOverdue?.toLocaleString()} pending`} />
        <StatCard label="Upcoming" value={summary.upcomingCount?.toLocaleString()} color="#fd7e14" sub={`${summary.totalUpcoming?.toLocaleString()} expected`} />
        <StatCard label="Sevas" value={summary.sevaCount?.toLocaleString()} color="#6f42c1" />
        <StatCard label="Points Issued" value={summary.pointsAdded?.toLocaleString()} color="#20c997" sub={`${summary.pointsRedeemed} redeemed`} />
      </div>

      {/* Charts Row 1 */}
      <div style={{display:"flex",gap:16,marginBottom:16,flexWrap:"wrap"}}>
        <div style={{flex:1,minWidth:300,background:"#fff",borderRadius:12,padding:16,boxShadow:"0 2px 8px rgba(0,0,0,0.07)"}}>
          <Chart type="bar" height={280}
            series={[{name:"New Users",data:userMonthData}]}
            options={{chart:{toolbar:{show:false}},title:{text:"New Users by Month",style:{fontSize:"13px"}},xaxis:{categories:monthLabels},colors:["#007bff"],dataLabels:{enabled:true,style:{fontSize:'10px',colors:["#333"]},offsetY:-20,formatter:function(val){return val.toLocaleString()}},plotOptions:{bar:{columnWidth:"50%",dataLabels:{position:'top'}}}}}
          />
        </div>
        <div style={{flex:1,minWidth:300,background:"#fff",borderRadius:12,padding:16,boxShadow:"0 2px 8px rgba(0,0,0,0.07)"}}>
          <Chart type="bar" height={280}
            series={[{name:"Donations",data:paymentMonthData}]}
            options={{chart:{toolbar:{show:false}},title:{text:"Donations by Month",style:{fontSize:"13px"}},xaxis:{categories:monthLabels},colors:["#28a745"],dataLabels:{enabled:true,style:{fontSize:'10px',colors:["#333"]},offsetY:-20,formatter:function(val){return val.toLocaleString()}},plotOptions:{bar:{columnWidth:"50%",dataLabels:{position:'top'}}}}}
          />
        </div>
      </div>

      {/* Charts Row 2 */}
      <div style={{display:"flex",gap:16,marginBottom:16,flexWrap:"wrap"}}>
        <div style={{flex:1,minWidth:280,background:"#fff",borderRadius:12,padding:16,boxShadow:"0 2px 8px rgba(0,0,0,0.07)"}}>
          <Chart type="pie" height={280}
            series={Object.values(usersByContinent||{})}
            options={{chart:{toolbar:{show:false}},title:{text:"Users by Continent",style:{fontSize:"13px"}},labels:Object.keys(usersByContinent||{}),legend:{position:"bottom"}}}
          />
        </div>
        <div style={{flex:1,minWidth:280,background:"#fff",borderRadius:12,padding:16,boxShadow:"0 2px 8px rgba(0,0,0,0.07)"}}>
          <Chart type="donut" height={280}
            series={Object.values(paymentsByCurrency||{})}
            options={{chart:{toolbar:{show:false}},title:{text:"Donations by Currency",style:{fontSize:"13px"}},labels:Object.keys(paymentsByCurrency||{}),legend:{position:"bottom"}}}
          />
        </div>
        <div style={{flex:1,minWidth:280,background:"#fff",borderRadius:12,padding:16,boxShadow:"0 2px 8px rgba(0,0,0,0.07)"}}>
          <Chart type="donut" height={280}
            series={Object.values(paymentsByMethod||{})}
            options={{chart:{toolbar:{show:false}},title:{text:"Payment Methods",style:{fontSize:"13px"}},labels:Object.keys(paymentsByMethod||{}),legend:{position:"bottom"}}}
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
